import { Close as CloseIcon, Store as StoreIcon } from "@mui/icons-material";
import {
  Box,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useState } from "react";
import { Shop } from "../../../../../types/shop";

interface ShopSearchControlProps {
  onSearch: (query: string) => void;
  results: Shop[];
  onResultSelect: (shop: Shop) => void;
  onClose: () => void;
}

const ShopSearchControl: React.FC<ShopSearchControlProps> = ({
  onSearch,
  results,
  onResultSelect,
  onClose,
}) => {
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState("");
  const accentColor = theme.palette.success.main;
  const inputBgColor =
    theme.palette.mode === "light"
      ? "rgba(237, 247, 237, 0.96)"
      : "rgba(20, 45, 28, 0.96)";

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    onSearch(value);
  };

  const normalizedQuery = searchQuery.trim().toLowerCase();
  const matchingResults = normalizedQuery
    ? results.filter(
        (shop) =>
          shop.name.toLowerCase().includes(normalizedQuery) ||
          shop.address?.toLowerCase().includes(normalizedQuery) ||
          shop.description?.toLowerCase().includes(normalizedQuery)
      )
    : [];

  const hasQuery = normalizedQuery.length > 0;

  const handleResultClick = (shop: Shop) => {
    setSearchQuery(shop.name);
    onSearch(shop.name);
    onResultSelect(shop);
  };

  const handleResultsWheel = (event: React.WheelEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.scrollTop += event.deltaY;
  };

  return (
    <Box
      sx={{
        width: 260,
      }}
    >
      {hasQuery && (
        <Paper
          elevation={4}
          onWheelCapture={handleResultsWheel}
          sx={{
            mb: 0.5,
            maxHeight: 190,
            overflowY: "auto",
            overscrollBehavior: "contain",
            border: "1px solid",
            borderColor: accentColor,
            backgroundColor:
              theme.palette.mode === "light"
                ? "rgba(255, 255, 255, 0.96)"
                : "rgba(18, 18, 18, 0.96)",
          }}
        >
          {matchingResults.length > 0 ? (
            <List dense disablePadding>
              {matchingResults.map((shop) => (
                <ListItemButton
                  key={shop.id}
                  onClick={() => handleResultClick(shop)}
                  sx={{ py: 0.5, px: 1 }}
                >
                  <ListItemText
                    primary={shop.name}
                    secondary={shop.address}
                    primaryTypographyProps={{
                      fontSize: "0.78rem",
                      lineHeight: 1.2,
                      noWrap: true,
                    }}
                    secondaryTypographyProps={{
                      fontSize: "0.68rem",
                      lineHeight: 1.2,
                      noWrap: true,
                    }}
                  />
                </ListItemButton>
              ))}
            </List>
          ) : (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", px: 1, py: 0.75 }}
            >
              No shops found
            </Typography>
          )}
        </Paper>
      )}
      <Box sx={{ position: "relative" }}>
        <TextField
          fullWidth
          size="small"
          value={searchQuery}
          onChange={handleSearch}
          placeholder="Search shops..."
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <StoreIcon sx={{ color: accentColor, fontSize: 18 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            backgroundColor: inputBgColor,
            borderRadius: 1,
            boxShadow: `0 2px 8px ${
              theme.palette.mode === "light"
                ? "rgba(46, 125, 50, 0.22)"
                : "rgba(102, 187, 106, 0.18)"
            }`,
            "&:hover": {
              boxShadow: `0 3px 10px ${
                theme.palette.mode === "light"
                  ? "rgba(46, 125, 50, 0.3)"
                  : "rgba(102, 187, 106, 0.24)"
              }`,
            },
            "& .MuiOutlinedInput-root": {
              fontSize: "0.8rem",
              minHeight: 34,
              "& fieldset": {
                borderColor: accentColor,
                borderWidth: 1.5,
              },
              "&:hover fieldset": {
                borderColor: accentColor,
              },
              "&.Mui-focused fieldset": {
                borderColor: accentColor,
                borderWidth: 2,
              },
            },
          }}
        />
        <IconButton
          size="small"
          aria-label="Hide shop pins"
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 0,
            right: 0,
            transform: "translate(50%, -50%)",
            width: 20,
            height: 20,
            bgcolor: "background.paper",
            color: accentColor,
            border: "1px solid",
            borderColor: accentColor,
            boxShadow: 2,
            zIndex: 1,
            "&:hover": {
              bgcolor: inputBgColor,
            },
          }}
        >
          <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Box>
    </Box>
  );
};

export default ShopSearchControl;
